import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import "./home.css";

// PrebidAI Logo Component
const PrebidAILogo = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Brain outline */}
    <path 
      d="M50 10 C 35 10, 25 18, 25 30 C 25 35, 22 38, 20 42 C 18 46, 18 52, 22 56 C 24 58, 26 62, 26 68 C 26 78, 35 88, 50 90 C 65 88, 74 78, 74 68 C 74 62, 76 58, 78 56 C 82 52, 82 46, 80 42 C 78 38, 75 35, 75 30 C 75 18, 65 10, 50 10 Z" 
      stroke="#667eea" 
      strokeWidth="3" 
      fill="#f0f4ff"
    />
    
    {/* AI Neural network nodes */}
    <circle cx="40" cy="35" r="4" fill="#667eea" />
    <circle cx="60" cy="35" r="4" fill="#667eea" />
    <circle cx="35" cy="50" r="4" fill="#764ba2" />
    <circle cx="50" cy="50" r="4" fill="#667eea" />
    <circle cx="65" cy="50" r="4" fill="#764ba2" />
    <circle cx="40" cy="65" r="4" fill="#667eea" />
    <circle cx="60" cy="65" r="4" fill="#667eea" />
    
    {/* Neural network connections */}
    <line x1="40" y1="35" x2="35" y2="50" stroke="#667eea" strokeWidth="2" opacity="0.4" />
    <line x1="40" y1="35" x2="50" y2="50" stroke="#667eea" strokeWidth="2" opacity="0.4" />
    <line x1="60" y1="35" x2="50" y2="50" stroke="#667eea" strokeWidth="2" opacity="0.4" />
    <line x1="60" y1="35" x2="65" y2="50" stroke="#667eea" strokeWidth="2" opacity="0.4" />
    <line x1="35" y1="50" x2="40" y2="65" stroke="#764ba2" strokeWidth="2" opacity="0.4" />
    <line x1="50" y1="50" x2="40" y2="65" stroke="#667eea" strokeWidth="2" opacity="0.4" />
    <line x1="50" y1="50" x2="60" y2="65" stroke="#667eea" strokeWidth="2" opacity="0.4" />
    <line x1="65" y1="50" x2="60" y2="65" stroke="#764ba2" strokeWidth="2" opacity="0.4" />
    
    {/* Price tag symbol */}
    <path 
      d="M 68 22 L 82 22 L 88 28 L 88 42 L 82 48 L 68 48 L 68 22 Z" 
      fill="#ffd700" 
      stroke="#e6b800" 
      strokeWidth="1.5"
    />
    <circle cx="78" cy="35" r="3" fill="#e6b800" />
  </svg>
);

export default function Home() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [contactForm, setContactForm] = React.useState({
    name: '',
    email: '',
    message: ''
  });
  const [demoForm, setDemoForm] = React.useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    preferredDate: '',
    message: ''
  });
  const [formStatus, setFormStatus] = React.useState({ type: '', message: '' });
  const [demoFormStatus, setDemoFormStatus] = React.useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDemoSubmitting, setIsDemoSubmitting] = React.useState(false);

  // If already logged in, redirect to dashboard
  React.useEffect(() => {
    if (currentUser) {
      navigate("/dashboard");
    }
  }, [currentUser, navigate]);

  // Scroll to demo section
  const scrollToDemo = () => {
    const demoSection = document.querySelector('.demo-section');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Scroll to book demo section
  const scrollToBookDemo = () => {
    const bookDemoSection = document.querySelector('.book-demo-section');
    if (bookDemoSection) {
      bookDemoSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Handle contact form input changes
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle demo form input changes
  const handleDemoChange = (e) => {
    const { name, value } = e.target;
    setDemoForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/send-rfq-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setFormStatus({ type: 'error', message: data.error || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      setFormStatus({ type: 'error', message: 'An error occurred. Please try emailing us directly at prebidai@gmail.com' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle demo booking form submission
  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsDemoSubmitting(true);
    setDemoFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/send-rfq-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...demoForm,
          type: 'demo_booking'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setDemoFormStatus({ type: 'success', message: 'Demo booking request sent successfully! Our sales team will contact you soon.' });
        setDemoForm({ name: '', email: '', company: '', phone: '', preferredDate: '', message: '' });
      } else {
        setDemoFormStatus({ type: 'error', message: data.error || 'Failed to send booking request. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting demo booking:', error);
      setDemoFormStatus({ type: 'error', message: 'An error occurred. Please try emailing us directly at prebidai@gmail.com' });
    } finally {
      setIsDemoSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header/Navigation */}
      <header className="landing-header">
        <div className="landing-nav">
          <div className="logo-section">
            <PrebidAILogo size={40} />
            <span className="brand-name">PrebidAI</span>
          </div>
          <div className="nav-buttons">
            <button className="nav-btn" onClick={scrollToBookDemo}>
              Book Demo
            </button>
            <button className="nav-btn" onClick={() => navigate("/login")}>
              Sign In
            </button>
            <button className="nav-btn primary" onClick={() => navigate("/register")}>
              Get Started Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">🚀 AI-Powered RFQ Management</div>
          <h1 className="hero-title">
            Transform Your <span className="gradient-text">Bidding Process</span> with AI
          </h1>
          <p className="hero-subtitle">
            PrebidAI streamlines your Request for Quotation workflow with intelligent automation, 
            real-time tracking, and AI-powered proposal generation. Win more bids, faster.
          </p>
          <div className="hero-cta">
            <button className="cta-primary" onClick={() => navigate("/register")}>
              Start Free Trial
            </button>
            <button className="cta-secondary" onClick={scrollToDemo}>
              View Demo
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <div className="stat-number">10x</div>
              <div className="stat-label">Faster Quote Processing</div>
            </div>
            <div className="stat">
              <div className="stat-number">95%</div>
              <div className="stat-label">Response Rate</div>
            </div>
            <div className="stat">
              <div className="stat-number">50+</div>
              <div className="stat-label">Active Organizations</div>
            </div>
          </div>
        </div>
        <div className="hero-image">
          <div className="dashboard-preview">
            <PrebidAILogo size={120} />
            <div className="preview-text">
              <div className="preview-badge">✨ AI-Powered</div>
              <h3>Intelligent RFQ Management</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="demo-section">
        <div className="section-header">
          <h2>See PrebidAI in Action</h2>
          <p>Watch how our platform streamlines your RFQ workflow</p>
        </div>
        <div className="demo-video-container">
          <div className="video-wrapper">
            <iframe
              src="https://www.youtube.com/embed/TvczkV8jiHM"
              title="PrebidAI Demo Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="demo-video"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2>Everything You Need to Win More Bids</h2>
          <p>Powerful features designed for modern procurement teams</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📧</div>
            <h3>Email Automation</h3>
            <p>Automated RFQ email sending and tracking with instant push notifications</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">👥</div>
            <h3>Vendor Management</h3>
            <p>Comprehensive vendor database with subsystem-based workflow</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Real-time Tracking</h3>
            <p>Live monitoring of vendor responses with automated scoring</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💰</div>
            <h3>Quote Comparison</h3>
            <p>Automated quote analysis and winner selection</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📝</div>
            <h3>AI Proposal Writer</h3>
            <p>Generate professional proposals with AI-powered templates</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3>Secure & Compliant</h3>
            <p>Enterprise-grade security with audit trails and compliance tracking</p>
          </div>
        </div>
      </section>

      {/* Book a Demo Section */}
      <section className="book-demo-section">
        <div className="book-demo-container">
          <div className="book-demo-header">
            <h2>Book a Demo with Our Sales Team</h2>
            <p className="book-demo-description">
              See PrebidAI in action! Schedule a personalized demo with our sales team to discover how we can transform your RFQ workflow.
            </p>
          </div>

          <div className="book-demo-content">
            <div className="demo-benefits">
              <h3>What You'll Get:</h3>
              <div className="benefit-list">
                <div className="benefit-item">
                  <div className="benefit-icon">✅</div>
                  <div>
                    <h4>Personalized Walkthrough</h4>
                    <p>See how PrebidAI fits your specific needs</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✅</div>
                  <div>
                    <h4>Live Q&A Session</h4>
                    <p>Get all your questions answered by our experts</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✅</div>
                  <div>
                    <h4>Custom Implementation Plan</h4>
                    <p>Receive a tailored onboarding strategy</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✅</div>
                  <div>
                    <h4>Pricing Discussion</h4>
                    <p>Understand our flexible pricing options</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="demo-form-wrapper">
              <h3>Schedule Your Demo</h3>
              <form onSubmit={handleDemoSubmit} className="demo-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="demo-name">Full Name *</label>
                    <input
                      type="text"
                      id="demo-name"
                      name="name"
                      value={demoForm.name}
                      onChange={handleDemoChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="demo-email">Work Email *</label>
                    <input
                      type="email"
                      id="demo-email"
                      name="email"
                      value={demoForm.email}
                      onChange={handleDemoChange}
                      required
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="demo-company">Company Name *</label>
                    <input
                      type="text"
                      id="demo-company"
                      name="company"
                      value={demoForm.company}
                      onChange={handleDemoChange}
                      required
                      placeholder="Your Company"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="demo-phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="demo-phone"
                      name="phone"
                      value={demoForm.phone}
                      onChange={handleDemoChange}
                      required
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="demo-date">Preferred Date & Time *</label>
                  <input
                    type="datetime-local"
                    id="demo-date"
                    name="preferredDate"
                    value={demoForm.preferredDate}
                    onChange={handleDemoChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="demo-message">Additional Requirements (Optional)</label>
                  <textarea
                    id="demo-message"
                    name="message"
                    value={demoForm.message}
                    onChange={handleDemoChange}
                    rows="4"
                    placeholder="Tell us about your specific needs or any questions you'd like us to address during the demo..."
                  />
                </div>
                {demoFormStatus.message && (
                  <div className={`form-status ${demoFormStatus.type}`}>
                    {demoFormStatus.message}
                  </div>
                )}
                <button 
                  type="submit" 
                  className="demo-submit-btn"
                  disabled={isDemoSubmitting}
                >
                  {isDemoSubmitting ? 'Submitting...' : 'Book Your Demo'}
                </button>
                <p className="form-note">
                  * Our sales team will contact you within 24 hours to confirm your demo session
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your RFQ Process?</h2>
          <p>Join leading organizations using PrebidAI to win more bids</p>
          <button className="cta-large" onClick={() => navigate("/register")}>
            Get Started Free
          </button>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="contact-section">
        <div className="contact-container">
          <div className="contact-info">
            <h2>Contact Us</h2>
            <p className="contact-description">
              Have questions about PrebidAI? We'd love to hear from you.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div>
                  <h3>Email Us</h3>
                  <a href="mailto:prebidai@gmail.com" className="contact-link">
                    prebidai@gmail.com
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">💬</div>
                <div>
                  <h3>Quick Response</h3>
                  <p>We typically respond within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <h3>Send us a message</h3>
            <form onSubmit={handleContactSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={contactForm.name}
                  onChange={handleContactChange}
                  required
                  placeholder="Your name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  required
                  rows="5"
                  placeholder="Tell us how we can help..."
                />
              </div>
              {formStatus.message && (
                <div className={`form-status ${formStatus.type}`}>
                  {formStatus.message}
                </div>
              )}
              <button 
                type="submit" 
                className="contact-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <PrebidAILogo size={32} />
            <span className="brand-name">PrebidAI</span>
          </div>
          <p>© 2024 PrebidAI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
